<template>
    <div v-if="isLoadingOrderData">
        <span class="icon mr-1">
            <FontAwesomeIcon icon="spinner" spin />
        </span>
        <span class="label">
            <small>
              Ordergegevens worden geladen&hellip;
            </small>
        </span>
    </div>

    <b-alert v-else-if="error" show variant="danger">
        <span class="icon mr-1">
            <FontAwesomeIcon icon="exclamation-triangle" class="text-danger" />
        </span>
        <span class="label">
            <small>{{error}}</small>
        </span>
    </b-alert>

    <div v-else>
        <page-title :title="pageTitle" icon="pe-7s-note2" :data-id="salesOrder.id">
            <template v-if="salesOrder.linked_sales_order_identifiers" v-slot:subTitle>
                Gelinkte orders:
                <span v-for="(identifier, index) in salesOrder.linked_sales_order_identifiers" class="linked-order" :key="index">
                      <router-link :to="{
                                        name: 'OrderDetailOverview',
                                        params: {
                                            salesOrderIdentifier: identifier,
                                        },
                                   }"
                                   v-text="identifier"
                      />
                      <span class="comma-separator"> , </span>
                  </span>
            </template>
        </page-title>
        <b-row>
            <b-col>
                <ul class="nav nav-tabs mb-4">
                    <li v-for="(tab, index) in visibleTabs" class="nav-item" :key="index">
                        <router-link tag="a"
                                     :to="{name:tab.routeName}"
                                     class="nav-link"
                                     :class="{active:isMatchedRoute(tab.matchRouteNames)}">
                            <i>{{ tab.name }}</i>
                        </router-link>
                    </li>
                </ul>
                <router-view :sales-order="salesOrder" :key="lastUpdate" @dataChanged="refreshOrder()"/>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import PageTitle from "@/components/PageTitle.vue"
import {checkDispatchRequestIsPickupableWithCode} from "@/helpers/dispatch-requests";

export default {
    name: "OrderDetailLayout",
    components: {PageTitle},
    props: {
        salesOrderIdentifier: {required: true},
    },

    data() {
        return {
            isLoadingOrderData: true,
            salesOrder: {},
            linkedSalesOrders: [],
            lastUpdate: '',
            error: null,
            visibleTabs: [],
        }
    },

    computed: {
        pageTitle() {
            return 'Order: ' + this.salesOrder.identifier
        },
        pageSubTitle() {
            if (!this.salesOrder.linked_sales_order_identifiers) {
                return ''
            }

            // this.salesOrder.linked_sales_order_identifiers
        },
        tabs() {
            return [
                {
                    routeName: 'OrderDetailOverview',
                    matchRouteNames: 'OrderDetailOverview',
                    name: 'Overzicht',
                    visible: () => this.hasPermission('orders.view'),
                },
                {
                    routeName: 'OrderDetailSalesOrderLines',
                    matchRouteNames: 'OrderDetailSalesOrderLines',
                    name: 'Orderregels',
                    visible: () => this.hasPermission('orders.view'),
                },
                {
                    routeName: '',
                    matchRouteNames: '',
                    name: 'Historie',
                    visible: () => false,
                },
                {
                    routeName: 'OrderDetailHoldReasons',
                    matchRouteNames: 'OrderDetailHoldReasons',
                    name: 'Blokkeringen',
                    visible: () => {
                        return this.hasPermission('orders.holdreview.view')
                    },
                },
                {
                    routeName: '',
                    matchRouteNames: '',
                    name: 'Retouren',
                    visible: () => false,
                },
                {
                    routeName: 'OrderDetailRefunds',
                    matchRouteNames: 'OrderDetailRefunds',
                    name: 'Crediteringen',
                    visible: () => {
                        return this.hasPermission('refunds.view')
                    },
                },
                {
                    routeName: 'OrderPickByStoreScan',
                    matchRouteNames: 'OrderPickByStoreScan',
                    name: 'Scannen',
                    visible: () => {
                        return this.$route.name === 'OrderPickByStoreScan'
                    },
                },
                {
                    routeName: 'OrderDetailHistory',
                    matchRouteNames: 'OrderDetailHistory',
                    name: 'Geschiedenis',
                    visible: () => {
                        return this.hasPermission('orders.history.view');
                    },
                },
                {
                    routeName: 'OrderDetailEventResults',
                    matchRouteNames: 'OrderDetailEventResults',
                    name: 'Uitgevoerde acties',
                    visible: () => {
                        return this.hasPermission('orders.triggers.view')
                    },
                },
                {
                    routeName: 'OrderDetailPickup',
                    matchRouteNames: 'OrderDetailPickup',
                    name: 'Afhalen',
                    visible: async () => {
                        if (!this.isFeatureEnabled('order-pickup-with-code')) {
                            return false
                        }

                        for (let drq of this.salesOrder?.current_dispatch_requests_with_paused ?? []) {
                            if (await checkDispatchRequestIsPickupableWithCode(drq)) {
                                return true
                            }
                        }

                        return false
                    },
                },
                {
                    routeName: 'OrderNextActionDecision',
                    matchRouteNames: 'OrderNextActionDecision',
                    name: 'Vervolgactiekeuze',
                    visible: () => {
                        return this.salesOrder?.current_dispatch_requests?.some(drq => drq.requires_next_action_decision)
                    },
                },
            ]
        },
    },

    async mounted() {
        await this.getOrder()

        this.checkVisibleTabs()
    },

    methods: {
        async getOrder() {
            this.error = false

            let includes = [
                'sales_order_lines',
                'sales_order_lines.promotions',
                'addresses',
                'latest_comment',
                'current_dispatch_requests_with_paused.dispatch_request_lines',
                'current_dispatch_requests_with_paused.dispatch_request_lines.presales_active',
                'current_dispatch_requests_with_paused.dispatch_request_lines.goods_receipt_line:id,goods_receipt_id',
                'current_dispatch_requests_with_paused.dispatch_request_lines.goods_receipt_line.goods_receipt:id,recipient_location_id,identifier',
                'current_dispatch_requests_with_paused.carrier_method',
                'current_dispatch_requests_with_paused.tracking_codes',
                'current_dispatch_requests_with_paused.dispatch_type',
                'current_dispatch_requests_with_paused.dispatch_request_batch',
                'current_dispatch_requests_with_paused.storage_location.storage_zone',
                'current_dispatch_requests_with_paused.responsible_location',
                'current_dispatch_requests_with_paused.responsible_location.location_type',
                'current_dispatch_requests_with_paused.cascades_into_dispatch_requests:id',
                'current_dispatch_requests_with_paused.box_suggestion.suggested_box',
                'sales_order_payments',
                'sales_order_payments.payment_service_provider_account.payment_service_provider',
                'sales_order_payments.payment_service_provider_account_method',
                'global_sales_order_status',
                'invoices',
                'responsible_location_history'
            ];

            const endpoint = `/api/rest/sales-orders/${this.salesOrderIdentifier}`
            const params = new URLSearchParams();

            if (window.location.hash.includes('include_rerouted_replaced_dispatch_requests=1') && this.hasPermission('orders.dispatchrequests.include.reroutedreplaceddispatchrequests')) {
                includes = includes.concat([
                    'last_rerouted_dispatch_request',
                ]);
                params.set('include_rerouted_replaced_dispatch_requests', 1)
            }
            params.set('include', includes.join(';'));

            return this.$http.get(endpoint, { params })
                .then(response => {
                    this.salesOrder = response.data
                    this.salesOrder.current_dispatch_requests = this.salesOrder.current_dispatch_requests_with_paused

                    this.lastUpdate = (new Date()).getTime()
                })
                .catch(error => {
                    switch (error.response.status) {
                        case 404:
                            this.error = "De gevraagde order bestaat niet."
                            break
                        case 422:
                            if (error.response.data.errors.some(e => e.identifier === 'NoDispatchRequestsForUser')) {
                                this.error = "Er zijn geen actieve leveringen (meer) bij deze order die geassocieerd zijn met de locaties waar je rechten toe hebt."
                            }
                            else {
                                this.error = "Onbekende validatiefout bij het ophalen van de order."
                            }
                            break
                        default:
                            this.error = "Er is iets misgegaan bij het ophalen van de order."
                            break
                    }
                })
                .finally(() => {
                    this.isLoadingOrderData = false
                })
        },
        refreshOrder() {
            this.getOrder()
        },
        isMatchedRoute(tabRouteNames) {
            let match = false
            _.each(this.$route.matched, route => {
                if (tabRouteNames.indexOf(route.name) !== -1)
                    match = true
            })
            return match
        },
        async checkVisibleTabs() {
            this.visibleTabs = []
            for (let tab of this.tabs) {
                let visible = tab.visible()

                if (visible instanceof Promise) {
                    visible = await visible
                }

                if (visible) {
                    this.visibleTabs.push(tab)
                }
            }
        },
    },

    watch: {
        '$route.name': {
            handler(val) {
                this.checkVisibleTabs()
            },
        }
    }
}
</script>

<style scoped>
.linked-order:last-of-type > .comma-separator {
    display: none;
}
</style>
